import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { FormGroup } from "@angular/forms";

import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";

import { CHECKBOX, LINK, RADIO } from "@hermes/aphrodite/dynamic-box";
import { BoxType } from "@hermes/api-model-product";
import { Settings } from "@hermes/app-core";

import {
    FeatureFlagFacade,
    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
} from "@hermes/states/flipper";
import {
    GridFacade,
    GridFilter,
    GridFilterGroup,
} from "@hermes/states/grid-filters-state";

import { stopEventPropagation } from "@hermes/utils-generic/helpers";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

import { getFacetQueryParameter } from "../../helpers/grid-filter.helper";

import { TypeFilter } from "../grid-filters/grid-filters.component";

const ARIA_LABEL_ITEM = $localize`:@@hermes.accessibility.item:item`;
const ARIA_LABEL_ITEMS = $localize`:@@hermes.accessibility.items:items`;

@Component({
    selector: "h-grid-filter-color-and-size",
    templateUrl: "./grid-filter-color-and-size.component.html",
    styleUrls: ["./grid-filter-color-and-size.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFilterColorAndSizeComponent implements OnInit, OnDestroy {
    @Input()
    public gridFilterGroup!: GridFilterGroup & { counterChecked: number };
    @Input()
    public filterGroupForm!: FormGroup;

    public isMobile: boolean = false;

    private isCombinedFacetActivated: boolean = false;
    private subscriptions = new Subscription();

    constructor(
        private gridFacade: GridFacade,
        private settings: Settings,
        private breakpointService: BreakpointService,
        private changeDetector: ChangeDetectorRef,
        private featureFlagFacade: FeatureFlagFacade,
        private activatedRoute: ActivatedRoute,
    ) {}

    public get isFilterTypeColors(): boolean {
        return this.gridFilterGroup.presentation === TypeFilter.COLORS;
    }

    public ngOnInit(): void {
        this.subscriptions.add(
            this.featureFlagFacade
                .isActivated(SEO_COMBINED_FACETS_ACTIVATION_ON_GRID)
                .subscribe((flag) => {
                    this.isCombinedFacetActivated = flag;
                }),
        );
        this.subscriptions.add(
            this.breakpointService
                .mediumBreakpointObserver()
                .subscribe((value: BreakpointState) => {
                    this.isMobile = !value.matches;
                    this.changeDetector.markForCheck();
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public getBoxtype(facetIsCrawlable: boolean): BoxType {
        const defaultBoxType = this.isFilterTypeColors ? CHECKBOX : RADIO;
        return facetIsCrawlable ? LINK : (defaultBoxType as BoxType);
    }

    public trackByGridFilter(index: number, gridFilter: GridFilter): string {
        return `${index}_${gridFilter?.name}`;
    }

    public onHandleClick(event: Event, filter: GridFilter): void {
        stopEventPropagation(event);
        if (this.getBoxtype(filter.facetIsCrawlable) === LINK) {
            // When we click on filtered category, we remove urlParams to avoid to send it to BCK
            filter.urlParams = undefined;

            filter.selected = true;
            filter.filterGroupName = this.gridFilterGroup.facetUrlParameterName;
        }
        this.gridFacade.updateGridFilter(filter, this.gridFilterGroup);
    }

    public getAsset(filter: GridFilter): string {
        const base = this.settings.assetsUrlProduct;
        const color = `MAIN_COLOR_${filter.value.toUpperCase()}`;

        return `${base}${color}?wid=50&hei=50`;
    }

    public getLabelValue(filter: GridFilter): string {
        return `${filter.name}${
            this.isFilterTypeColors ? ` (${filter.total})` : ""
        }`;
    }

    public getAriaLabel(filter: GridFilter): string {
        return filter.total > 1
            ? `${filter.name}, ${filter.total} ${ARIA_LABEL_ITEMS}`
            : `${filter.name}, ${filter.total} ${ARIA_LABEL_ITEM}`;
    }

    public getFacetUrl(
        filter: GridFilter,
        facetUrlParameterName: string,
    ): string {
        return `${window.location.pathname}?${getFacetQueryParameter(
            filter,
            facetUrlParameterName,
            this.activatedRoute.snapshot.queryParams,
            this.isCombinedFacetActivated,
        )}`;
    }
}
